import { Box, Chip, Stack, Typography } from "@mui/material";
import { FC } from "react";
import { ICPSectionTitle } from "../ICPSectionTitle";

interface MarketDisplayItem {
  /** Label text for the chip */
  label: string;
}

interface MarketDisplaySection {
  /** Section title (e.g., "INDUSTRY", "REVENUE") */
  title: string;
  /** Array of items to display as chips */
  items: MarketDisplayItem[];
  /** Message to show when no items are available */
  emptyMessage: string;
}

interface ICPMarketDisplayProps {
  /** Main title (e.g., "SAM", "SOM") */
  title: string;
  /** Array of sections to display */
  sections: MarketDisplaySection[];
  /** Message to show when no data is available */
  emptyMessage: string;
  /** Whether any data is available */
  hasData: boolean;
}

/**
 * Generic component for displaying market data (SAM/SOM) with sections and chips
 * Provides consistent styling and layout for both SAM and SOM displays
 */
export const ICPMarketDisplay: FC<ICPMarketDisplayProps> = ({
  title,
  sections,
  emptyMessage,
  hasData,
}) => {
  if (!hasData) {
    return (
      <Box>
        <ICPSectionTitle>{title}</ICPSectionTitle>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <ICPSectionTitle>{title}</ICPSectionTitle>
      <Stack gap={1.5} pt={2}>
        {sections.map((section, sectionIndex) => (
          <Box key={sectionIndex}>
            <Typography variant="subtitle2">{section.title}</Typography>

            {section.items.length > 0 ? (
              <Stack
                direction="row"
                flexWrap="wrap"
                gap={0.5}
                paddingY={0.5}
                maxWidth={500}
              >
                {section.items.map((item, itemIndex) => (
                  <Chip
                    key={itemIndex}
                    label={item.label}
                    variant="outlined"
                    size="small"
                    sx={{
                      fontSize: "14px",
                    }}
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2">{section.emptyMessage}</Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
